import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import BpmnEditor from './components/BpmnEditor/BpmnEditor';
import Toolbar from './components/Toolbar/Toolbar';
import { getModeler } from './utils/bpmnModeler';
import VideoPlayer from './components/VideoPlayer/VideoPlayer';
import HelpPanel from './components/HelpPanel/HelpPanel';
import ImageLightbox from './components/ImageLightbox/ImageLightbox';
import LinkModal from './components/LinkModal/LinkModal';
import { setOpenLightbox, setBpmnEventBus, setOpenLinkModal } from './utils/bridge';
import WrapSubProcessDialog from './components/WrapSubProcessDialog/WrapSubProcessDialog';
import { validateWrapSelection } from './custom/WrapInSubProcessProvider';
import { rehydrateSnapshotImages } from './utils/imageProcessing';
import { trackBpmnLoginEvent } from './utils/apiClient';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';

function App() {
  const [modeler, setModeler] = useState(null);
  const [mode, setMode] = useState('edit'); // 'edit' or 'view'
  const [videoPlayerOpen, setVideoPlayerOpen] = useState(false);
  const [hasVideoUrl, setHasVideoUrl] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [processName, setProcessName] = useState(null);
  const [authorName, setAuthorName] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [helpPanelOpen, setHelpPanelOpen] = useState(false);
  const [gridVisible, setGridVisible] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [lightboxElement, setLightboxElement] = useState(null);
  const [linkModalUrl, setLinkModalUrl] = useState(null);
  const [linkModalTitle, setLinkModalTitle] = useState('');
  const [wrapDialogOpen, setWrapDialogOpen] = useState(false);
  const [wrapElementCount, setWrapElementCount] = useState(0);
  const [wrapWarnings, setWrapWarnings] = useState([]);
  const [viewingVersion, setViewingVersion] = useState(null);
  const [viewingXml, setViewingXml] = useState(null);
  const [restoringXml, setRestoringXml] = useState(null);
  const currentXmlBackupRef = useRef(null);
  const videoSeekRef = useRef(null);
  const directoryHandleRef = useRef(null);
  const loginEventSentRef = useRef(false);

  // Session-persistent video state (survives VideoPlayer close/reopen)
  const [sessionVideoUrl, setSessionVideoUrl] = useState('');
  const [sessionVideoFileName, setSessionVideoFileName] = useState('');
  const [sessionVideoTime, setSessionVideoTime] = useState(0);
  const [sessionVideoPosition, setSessionVideoPosition] = useState(null);

  // Update browser tab title when process name changes
  useEffect(() => {
    document.title = processName ? `${processName} — BPMN Editor` : 'BPMN Editor';
  }, [processName]);

  // Warn user before leaving page with unsaved changes
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    if (isDirty) {
      window.addEventListener('beforeunload', handler);
    }
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // Get user info from MSAL
  const { instance: msalInstance, accounts } = useMsal();
  useEffect(() => {
    if (accounts.length > 0) {
      const account = accounts[0];
      if (msalInstance?.setActiveAccount) {
        msalInstance.setActiveAccount(account);
      }
      setAuthorName(account.name || account.username || 'Unbekannt');
      setIsAuthenticated(true);

      if (!loginEventSentRef.current) {
        loginEventSentRef.current = true;
        trackBpmnLoginEvent({ msalInstance, account }).catch(() => {
          // Tracking must not block editor usage.
        });
      }
    }
  }, [accounts, msalInstance]);

  const handleLogout = useCallback(() => {
    if (msalInstance) {
      msalInstance.logoutRedirect();
    }
  }, [msalInstance]);

  const handleModelerReady = useCallback((modelerInstance) => {
    setModeler(modelerInstance);
    // Fresh import = no unsaved changes yet
    setIsDirty(false);

    // Clean up session video state from previous diagram
    setSessionVideoUrl((prev) => {
      if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev);
      return '';
    });
    setSessionVideoFileName('');
    setSessionVideoTime(0);
    setSessionVideoPosition(null);

    // Check if definitions already have a video URL
    try {
      const definitions = modelerInstance.getDefinitions();
      if (definitions) {
        let url;
        try { url = definitions.get('custom:videoUrl'); } catch { /* moddle may not support get */ }
        if (!url) url = definitions.$attrs?.['custom:videoUrl'];
        setHasVideoUrl(!!url);
      }
    } catch { /* definitions not ready yet */ }

    // Listen for "wrap in sub-process" requests (keyboard shortcut / popup menu)
    try {
      const eventBus = modelerInstance.get('eventBus');
      eventBus.on('wrapInSubProcess.requestDialog', () => {
        try {
          const sel = modelerInstance.get('selection').get();
          setWrapElementCount(sel.length);
          setWrapWarnings(validateWrapSelection(sel));
        } catch { setWrapElementCount(0); setWrapWarnings([]); }
        setWrapDialogOpen(true);
      });
    } catch { /* */ }

    // Listen for seek events from overlays/properties panel
    try {
      const eventBus = modelerInstance.get('eventBus');
      eventBus.on('videoPlayer.seekTo', (e) => {
        setVideoPlayerOpen(true);
        // Store desired time so player can restore it even if mount is slow
        setSessionVideoTime(Number(e.timestamp) || 0);
        setTimeout(() => {
          if (videoSeekRef.current) {
            videoSeekRef.current(e.timestamp);
          }
        }, 300);
      });
      // Expose eventBus via module bridge for VideoGroup actions
      setBpmnEventBus(eventBus);
    } catch { /* viewer may not have eventBus */ }

    // Track unsaved changes via commandStack
    try {
      const eventBus = modelerInstance.get('eventBus');
      eventBus.on('commandStack.changed', () => {
        setIsDirty(true);
      });
    } catch { /* viewer has no commandStack */ }

    // Keyboard shortcut: Ctrl+G to wrap selection in sub-process
    const handleWrapShortcut = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'g') {
        e.preventDefault();
        try {
          const sel = modelerInstance.get('selection').get();
          if (sel.length >= 2) {
            setWrapElementCount(sel.length);
            setWrapWarnings(validateWrapSelection(sel));
            setWrapDialogOpen(true);
          }
        } catch { /* */ }
      }
    };
    document.addEventListener('keydown', handleWrapShortcut);
    // Store cleanup ref
    modelerInstance.__wrapShortcutCleanup = () => {
      document.removeEventListener('keydown', handleWrapShortcut);
    };
  }, []);

  const handleModeChange = useCallback(() => {
    if (mode === 'view') {
      // Switching to edit — always restore current version
      if (viewingVersion) {
        const backup = currentXmlBackupRef.current;
        currentXmlBackupRef.current = null;
        setViewingVersion(null);
        setViewingXml(null);
        if (backup) {
          setRestoringXml(backup);
        }
      }
      setMode('edit');
    } else {
      setMode('view');
    }
  }, [mode, viewingVersion]);

  const handleToggleVideo = useCallback(() => {
    setVideoPlayerOpen((prev) => !prev);
  }, []);

  // --- Session Video Callbacks ---
  const handleSessionVideoLoaded = useCallback((url, fileName) => {
    // Revoke previous blob URL if replacing with a new one
    setSessionVideoUrl((prev) => {
      if (prev && prev !== url && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
      return url;
    });
    setSessionVideoFileName(fileName || '');
    setSessionVideoTime(0);
  }, []);

  const handleSessionTimeUpdate = useCallback((time) => {
    setSessionVideoTime(time);
  }, []);

  const handleSessionPositionChange = useCallback((pos) => {
    setSessionVideoPosition(pos);
  }, []);

  const handleToggleHelp = useCallback(() => {
    setHelpPanelOpen((prev) => !prev);
  }, []);

  const handleToggleGrid = useCallback(() => {
    setGridVisible((prev) => !prev);
  }, []);

  // --- Version View ---

  /** Resolve image references in a stripped XML snapshot using the imageStore. */
  const resolveSnapshotImages = useCallback((xml, imageRefs) => {
    try {
      const instance = modeler || getModeler();
      let imageStore = {};
      if (instance?.getDefinitions) {
        const definitions = instance.getDefinitions();
        if (definitions) {
          let storeJson;
          try { storeJson = definitions.get('custom:imageStore'); } catch { /* */ }
          if (!storeJson) storeJson = definitions.$attrs?.['custom:imageStore'];
          if (storeJson) imageStore = JSON.parse(storeJson);
        }
      }
      return rehydrateSnapshotImages(xml, imageRefs, imageStore);
    } catch (e) {
      console.warn('Could not rehydrate snapshot images:', e);
      return xml;
    }
  }, [modeler]);

  const handleViewVersion = useCallback(async (xml, versionLabel, imageRefs) => {
    // Back up current XML before switching (only on first view, not when switching between versions)
    if (!viewingVersion) {
      const instance = modeler;
      if (instance?.saveXML) {
        try {
          const result = await instance.saveXML({ format: true });
          currentXmlBackupRef.current = result.xml;
        } catch { /* backup failed, will still try to show version */ }
      }
    }

    // Rehydrate images from the image store if the snapshot has image refs
    const hasImageRefs = imageRefs && Object.keys(imageRefs).length > 0;
    const rehydratedXml = hasImageRefs ? resolveSnapshotImages(xml, imageRefs) : xml;

    setViewingVersion(versionLabel);
    setViewingXml(rehydratedXml);

    // Switch to view mode if not already
    if (mode !== 'view') {
      setMode('view');
    }
  }, [modeler, mode, viewingVersion, resolveSnapshotImages]);

  const handleExitVersionView = useCallback(() => {
    setViewingVersion(null);

    const backup = currentXmlBackupRef.current;
    currentXmlBackupRef.current = null;

    if (backup) {
      // Reload the current XML into the viewer (stay in view mode)
      setViewingXml(backup);
    } else {
      setViewingXml(null);
    }
  }, []);

  // --- Image Lightbox ---
  const handleOpenLightbox = useCallback((imageUrl, element) => {
    setLightboxImage(imageUrl);
    setLightboxElement(element || null);
  }, []);

  const handleCloseLightbox = useCallback(() => {
    setLightboxImage(null);
    setLightboxElement(null);
  }, []);

  // --- Link Modal ---
  const handleOpenLinkModal = useCallback((url, title) => {
    setLinkModalUrl(url);
    setLinkModalTitle(title || url);
  }, []);

  const handleCloseLinkModal = useCallback(() => {
    setLinkModalUrl(null);
    setLinkModalTitle('');
  }, []);

  // --- Wrap in Sub-Process ---
  const handleWrapConfirm = useCallback(({ name, collapsed }) => {
    setWrapDialogOpen(false);
    try {
      const instance = modeler || getModeler();
      if (instance) {
        const eventBus = instance.get('eventBus');
        eventBus.fire('wrapInSubProcess.trigger', { name, collapsed });
      }
    } catch (err) {
      console.error('Wrap in sub-process failed:', err);
    }
  }, [modeler]);

  const handleWrapCancel = useCallback(() => {
    setWrapDialogOpen(false);
  }, []);

  const handleCropSave = useCallback((croppedDataUrl) => {
    if (!lightboxElement || !modeler) return;
    try {
      const modeling = modeler.get('modeling');
      modeling.updateModdleProperties(lightboxElement, lightboxElement.businessObject, {
        'custom:imageUrl': croppedDataUrl
      });
    } catch (err) {
      console.error('Error saving cropped image:', err);
    }
    setLightboxImage(croppedDataUrl);
  }, [lightboxElement, modeler]);

  // Expose lightbox opener via module bridge for bpmn-js custom modules
  useEffect(() => {
    setOpenLightbox(handleOpenLightbox);
    return () => { setOpenLightbox(null); };
  }, [handleOpenLightbox]);

  // Expose link modal opener via module bridge for bpmn-js custom modules
  useEffect(() => {
    setOpenLinkModal(handleOpenLinkModal);
    return () => { setOpenLinkModal(null); };
  }, [handleOpenLinkModal]);

  return (
    <div className="app-container">
      <Toolbar
        modeler={modeler}
        mode={mode}
        onModeChange={handleModeChange}
        onToggleVideo={handleToggleVideo}
        videoPlayerOpen={videoPlayerOpen}
        hasVideoUrl={hasVideoUrl}
        onModelerReady={handleModelerReady}
        isDirty={isDirty}
        setIsDirty={setIsDirty}
        processName={processName}
        setProcessName={setProcessName}
        directoryHandleRef={directoryHandleRef}
        authorName={authorName}
        isAuthenticated={isAuthenticated}
        onLogout={handleLogout}
        helpPanelOpen={helpPanelOpen}
        onToggleHelp={handleToggleHelp}
        gridVisible={gridVisible}
        onToggleGrid={handleToggleGrid}
        viewingVersion={viewingVersion}
        onViewVersion={handleViewVersion}
        onExitVersionView={handleExitVersionView}
      />
      <div className="editor-container">
        <BpmnEditor
          onModelerReady={handleModelerReady}
          mode={mode}
          modeler={modeler}
          viewingVersion={viewingVersion}
          onExitVersionView={handleExitVersionView}
          onViewVersion={handleViewVersion}
          viewingXml={viewingXml}
          restoringXml={restoringXml}
          onRestoreComplete={() => setRestoringXml(null)}
          gridVisible={gridVisible}
        />
        <HelpPanel isOpen={helpPanelOpen} onClose={handleToggleHelp} />
      </div>
      {videoPlayerOpen && modeler && (
        <VideoPlayer
          modeler={modeler}
          onClose={() => setVideoPlayerOpen(false)}
          onSeek={videoSeekRef}
          onVideoUrlChange={setHasVideoUrl}
          sessionVideoUrl={sessionVideoUrl}
          sessionVideoFileName={sessionVideoFileName}
          sessionVideoTime={sessionVideoTime}
          sessionVideoPosition={sessionVideoPosition}
          onSessionVideoLoaded={handleSessionVideoLoaded}
          onSessionTimeUpdate={handleSessionTimeUpdate}
          onSessionPositionChange={handleSessionPositionChange}
        />
      )}
      <ImageLightbox
        imageUrl={lightboxImage}
        isOpen={!!lightboxImage}
        onClose={handleCloseLightbox}
        isEditMode={mode === 'edit'}
        onCropSave={handleCropSave}
      />
      <LinkModal
        url={linkModalUrl}
        title={linkModalTitle}
        isOpen={!!linkModalUrl}
        onClose={handleCloseLinkModal}
      />
      <WrapSubProcessDialog
        isOpen={wrapDialogOpen}
        onConfirm={handleWrapConfirm}
        onCancel={handleWrapCancel}
        elementCount={wrapElementCount}
        warnings={wrapWarnings}
      />
    </div>
  );
}

export default App;